import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import { isUnauthorizedError } from "@/lib/authUtils";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ProcessingStatus } from "./ProcessingStatus";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Upload, 
  FileText, 
  File, 
  Image,
  Sparkles
} from "lucide-react";

interface FileUploadProps {
  onProcessingComplete?: (jobId: number) => void;
}

export function FileUpload({ onProcessingComplete }: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingJobId, setProcessingJobId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      try {
        const response = await apiRequest("POST", "/api/upload", formData);
        clearInterval(progressInterval);
        setUploadProgress(100);
        return response.json();
      } catch (error) {
        clearInterval(progressInterval);
        setUploadProgress(0);
        throw error;
      }
    },
    onSuccess: (data) => {
      setProcessingJobId(data.jobId);
      toast({
        title: "Upload Successful",
        description: "Your file has been uploaded and processing has started.",
      });
      // Invalidate processing queries to show new job immediately
      queryClient.invalidateQueries({ queryKey: ["/api/processing"] });
      // Reset upload progress after a short delay
      setTimeout(() => setUploadProgress(0), 1000);
    },
    onError: (error) => {
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: "Unauthorized",
          description: "Please log in to continue.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      toast({
        title: "Upload Failed",
        description: (error as Error).message,
        variant: "destructive",
      });
    },
  });

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Unsupported File Type",
        description: "Please upload a PDF, Word, Excel, or image file.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast({
        title: "File Too Large",
        description: "Please upload a file smaller than 10MB.",
        variant: "destructive",
      });
      return;
    }

    uploadMutation.mutate(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleProcessingComplete = (jobId: number) => {
    setProcessingJobId(null);
    onProcessingComplete?.(jobId);
    queryClient.invalidateQueries({ queryKey: ["/api/surveys"] });
  };

  // Show processing status if we have a job ID
  if (processingJobId) {
    return (
      <ProcessingStatus 
        jobId={processingJobId} 
        onComplete={handleProcessingComplete}
        onCancel={() => setProcessingJobId(null)}
      />
    );
  }

  return (
    <Card className="shadow-lg border-0 bg-card/50 backdrop-blur-sm">
      <CardContent className="p-8">
        {/* Upload Zone */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className={`
            relative border-2 border-dashed rounded-xl p-8 text-center 
            transition-all duration-200 cursor-pointer overflow-hidden
            ${isDragging 
              ? "border-primary bg-primary/5" 
              : "border-border hover:border-primary/50 hover:bg-primary/5"
            }
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {/* Decorative gradient blob */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
          
          <div className="relative z-10">
            {/* Icon with animated ring */}
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6 relative">
              <Upload className="h-10 w-10 text-primary" />
              <div className="absolute inset-0 rounded-full border-2 border-primary/20 animate-ping" />
            </div>
            
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Upload Your Survey Document
            </h3>
            <p className="text-muted-foreground mb-6">
              Drag and drop your file or click to browse
            </p>
            
            {/* Supported Formats - redesigned badges */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              <Badge variant="secondary" className="gap-1.5 px-3 py-1">
                <FileText className="h-3.5 w-3.5 text-red-500" />
                PDF
              </Badge>
              <Badge variant="secondary" className="gap-1.5 px-3 py-1">
                <FileText className="h-3.5 w-3.5 text-blue-500" />
                Word
              </Badge>
              <Badge variant="secondary" className="gap-1.5 px-3 py-1">
                <File className="h-3.5 w-3.5 text-green-500" />
                Excel
              </Badge>
              <Badge variant="secondary" className="gap-1.5 px-3 py-1">
                <Image className="h-3.5 w-3.5 text-purple-500" />
                Image
              </Badge>
            </div>
            
            <Button 
              size="lg" 
              className="gap-2"
              disabled={uploadMutation.isPending}
            >
              <Sparkles className="h-4 w-4" />
              {uploadMutation.isPending ? "Uploading..." : "Choose File"}
            </Button>
            
            <p className="text-xs text-muted-foreground mt-4">
              Maximum file size: 10MB
            </p>
          </div>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </motion.div>

        {/* Upload Progress - enhanced styling */}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 bg-muted/50 rounded-lg"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Uploading...</span>
              <span className="text-sm text-muted-foreground">{Math.round(uploadProgress)}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
}
